import config from '~/shared/config';
import apiConfig from '~/shared/config/api';
import type { IReqConfig, IResponse } from '~/shared/interfaces/api';
import type { IAuthSuccessData } from '~/shared/interfaces/auth';
import type { H3Event, EventHandlerRequest } from 'h3';
import {useConfigStore} from "~/shared/store/modules/config";
import type {IServerConfig} from "~/shared/interfaces/config";
import { useClientToken } from '~/composables/use-client-token';


const { auth } = apiConfig;
const loginPath = '/';


const jsonToQuery = function (obj: {[key: string]: any}, prefix?: string):string {
  let str = [], p;
  for (p in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, p)) {
      let k = prefix ? prefix + "[" + p + "]" : p,
        v = obj[p];

      str.push(
        v !== null && typeof v === "object"
          ? jsonToQuery(v, k)
          : encodeURIComponent(k) + "=" + encodeURIComponent(v)
      );

    }
  }
  if (str.length) {
    return str.join("&");
  }
  return '';
};

function setToken(tokenData?: {
  accessToken: string,
  opts: {
    expires: Date,
  },
}, event?: H3Event<EventHandlerRequest>) {
  if (!event) {
    const tokenFromCookie = useCookie('token', tokenData?.opts);
    tokenFromCookie.value = tokenData?.accessToken || null;
  } else {
    setCookie(event, 'token', tokenData?.accessToken || '', tokenData?.opts);
  }
}

function getQueryParams(query?: object) {
  if (query) {
    return '?' + jsonToQuery(query);
  }
  return '';
}

async function customFetch<T>(opts: IReqConfig, event?: H3Event<EventHandlerRequest>): Promise<IResponse<T>> {
  const configStore = useConfigStore();
  const environmentConfig = configStore.config as IServerConfig | null;
  const userAgent = event ? getCookie(event, 'user-agent') : useCookie('user-agent').value
  let headers: {[key: string]: string} = {
    Accept: 'application/json',
  }
  if(userAgent) {
    headers['User-Agent'] = userAgent;
  }

  const clientToken = useClientToken();
  headers['CLIENT-TOKEN'] = clientToken;

  if (!environmentConfig?.apiBaseUrl) {
    throw new Error('API base url not set!')
  }

  const apiFetch = $fetch.create({
    baseURL: environmentConfig?.apiBaseUrl,
    headers: headers,
    retry: 1,
    retryDelay: 3000,
    retryStatusCodes: [500, 502, 503, 504],
  });

  const { method, body, query, path } = opts;
  const token = event ? getCookie(event, 'token') : useCookie<string | undefined>('token').value;
  let result: IResponse<T> = {
    success: false,
    status: 0,
    data: null,
    errors: []
  }

  try {
    let emptyResResult: IResponse<T> | null = null;
    const res = await apiFetch<IResponse<T>>(path + getQueryParams(query), {
      method,
      body,
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
      async onResponseError({ response }) {
        if (response.status == 401 && !response.url.includes('refresh') && token) {
          const tokenRefreshed = await customFetch<IAuthSuccessData>(auth.refreshToken(), event);
          if (tokenRefreshed?.data && tokenRefreshed.success) {
            const { expires, accessToken } = tokenRefreshed.data;

            setToken({
              accessToken,
              opts: {
                expires: new Date(expires),
              },
            }, event);

            result = await customFetch<T>(opts, event);
            return;
          }
          setToken(undefined, event);
          if (process.client) {
            await navigateTo(loginPath);
            return;
          }
        }
      },
      onResponse({ response }) {
        if (response.status == 204) {
          emptyResResult = {
            success: true,
            status: response.status ?? null,
            data: null,
          }
        }
      }
    })
      .then((resData): IResponse<T> => {
        return resData
      })
      .catch(({ response }): IResponse<T> => {
        return {
          success: false,
          status: response.status ?? null,
          data: null,
          errors: response._data.errors
        }
      })
    return res ?? emptyResResult;
  } catch (error) {
    result.status = 500;
    return result;
  }
}

export default customFetch;
